import {
  CompletionType,
  completionTypes,
  DocumentFormat,
  DocumentVariable,
  FieldsSection,
  PatientDocument,
} from "@/src/data/allTypes";
import {
  decodeAndParseBase64,
  getUserId,
  jsonAbstract,
  uid,
} from "@/src/utils/utils";
import { useEffect, useState } from "react";

import { LuRedo, LuUndo } from "react-icons/lu";
import { TbVariablePlus } from "react-icons/tb";

import {
  ConditionGroupPicker,
  ConditionReplacementPicker,
} from "./documentConditions";

import { Button } from "@/src/components/buttons";
import {
  closeDialog,
  closeDrawer,
  openConfirmDialog,
  openDialog,
  openDrawer,
} from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { printContent } from "@/src/utils/utils";
import { FiltersPicker } from "../statistics/statisticsPage";
import { VariablesChoiceList } from "./variablesChoiceList";
import {
  findAllVariables,
  getVariableSpan,
  VariableOptions,
} from "./variablesHook";
import { compteRendu1 } from "@/src/data/data";
import { AiAudioDocumentEditorButton } from "./aiAudioDocumentEditorButton";

export type DocumentToolbarButtonType =
  | "format"
  | "size"
  | "bold"
  | "underline"
  | "align"
  | "undo"
  | "redo"
  | "variable"
  | "html"
  | "validate"
  | "save"
  | "print"
  | "ai";

function DocumentEditorToolbar({
  contentRef,
  toolbarId,
  patientDocument,
  isHtmlMode,
  allowedButtons,
  sections,
  onSave,
  onPrint,
  onValidate,
  onFormatChange,
  onHtmlModeChange,
  onChange,
}: {
  contentRef: React.RefObject<HTMLDivElement>;
  toolbarId?: string;
  patientDocument: PatientDocument | null | undefined;
  isHtmlMode: boolean;
  allowedButtons: DocumentToolbarButtonType[];
  sections?: (FieldsSection | null | undefined)[] | null | undefined;
  onSave?: () => void;
  onPrint?: () => void;
  onValidate?: () => void;
  onFormatChange: (newFormat: DocumentFormat) => void;
  onHtmlModeChange?: (newValue: boolean) => void;
  onChange: (newContent: string) => void;
}) {
  const [format, setFormat] = useState<DocumentFormat>("a4");
  const [status, setStatus] = useState(patientDocument?.status);

  async function handleClick(event: any) {
    const element = event.target.closest(".documentVariable");
    if (!element) return;

    let oldVariable = decodeAndParseBase64(
      element.getAttribute("data-value")
    ) as DocumentVariable | null;

    if (!oldVariable) return;
    let onConfirm = (newVariable: DocumentVariable | null) => {
      if (!newVariable) return;
      element.outerHTML = getVariableSpan(newVariable);
      onChange(contentRef.current?.innerHTML || "");
    };

    if (oldVariable.source == "statistics")
      return await openStatisticsReport({
        variable: jsonAbstract(oldVariable),
        onConfirm,
      });

    if (oldVariable.source != "condition")
      return await openDrawer({
        id: `documentVariableDialog${toolbarId}`,
        title: "Modifier variable",
        trailing: (
          <Button
            type={"tertiary"}
            color="red"
            onClick={() => {
              openConfirmDialog({
                id: "deleteVariableDialog",
                title: "Suppression",
                content: "Voulez-vous vraiment supprimer cette variable ?",
                onConfirm: () => {
                  element.outerHTML = "";
                  onChange(contentRef.current?.innerHTML || "");
                  closeDialog("deleteVariableDialog");
                  closeDrawer(`documentVariableDialog${toolbarId}`);
                },
              });
            }}
          >
            Supprimer
          </Button>
        ),
        additionalClasses: ["medium"],
        confirmBeforeClose: true,
        content: (
          <VariablesChoiceList
            currentVariable={oldVariable}
            allVariables={
              findAllVariables(contentRef.current?.innerHTML || "") || {}
            }
            sections={sections || []}
            onSelect={async (variable) => {
              await openVariableOptionsForm({
                variable,
                onConfirm: (variable) => {
                  onConfirm?.(variable);
                  closeDrawer(`documentVariableDialog${toolbarId}`);
                },
              });
            }}
          ></VariablesChoiceList>
        ),
      });

    await openVariableOptionsForm({
      element: element,
      variable: jsonAbstract(oldVariable),
      onConfirm,
    });
  }

  useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  async function openVariableSelect() {
    let selection = window.getSelection();
    let range = selection?.getRangeAt(0);
    let newNode = document.createElement(`span`);
    range?.insertNode(newNode);
    range?.selectNodeContents(newNode);
    let hasConfirmed = false;

    await openDrawer({
      id: `documentVariableDialog${toolbarId}`,
      title: "Ajouter variable",
      additionalClasses: ["medium"],
      confirmBeforeClose: true,
      content: (
        <VariablesChoiceList
          currentVariable={null}
          allVariables={
            findAllVariables(contentRef.current?.innerHTML || "") || {}
          }
          sections={sections || []}
          onSelect={async (variable) => {
            console.log("vaaariable", variable);
            await openVariableOptionsForm({
              variable,
              onConfirm: (variable) => {
                newNode.outerHTML = getVariableSpan(variable);
                selection?.removeAllRanges();
                onChange(contentRef.current?.innerHTML || "");
                closeDrawer(`documentVariableDialog${toolbarId}`);
                hasConfirmed = true;
              },
            });
          }}
        ></VariablesChoiceList>
      ),
    });
    if (!hasConfirmed) newNode.outerHTML = "";
  }

  async function openVariableOptionsForm({
    element,
    variable,
    onConfirm,
  }: {
    element?: any | null;
    variable: DocumentVariable;
    onConfirm?: (variable: DocumentVariable) => void;
  }) {
    if (variable.source == "condition")
      return await openConditionGroupPicker({ element, variable, onConfirm });

    await openDialog({
      id: `variableOptionsDialog${toolbarId}`,
      title: null,
      content: (
        <VariableOptions
          sections={sections || []}
          currentVariable={variable}
          onChange={(newValue) => {
            variable = newValue;
          }}
        ></VariableOptions>
      ),
      onConfirm: () => {
        onConfirm?.(variable);
        closeDialog(`variableOptionsDialog${toolbarId}`);
      },
    });
  }

  async function openConditionGroupPicker({
    element,
    variable,
    onConfirm,
  }: {
    element?: any;
    variable: DocumentVariable;
    onConfirm?: (newVariable: DocumentVariable) => void;
  }) {
    variable.conditionGroup ||= {
      id: uid(),
      name: "Condition",
      category: "conditionGroup",
      type: "and",
      creator: getUserId(),
      conditions: [
        {
          id: uid(),
          category: "condition",
          a: "",
          b: "",
          operator: "=",
          creator: getUserId(),
        },
      ],
    };

    await openDrawer({
      id: `documentConditionDialog${toolbarId}`,
      title: "Ajouter condition",
      trailing: (
        <div className="c2 gap10" style={{ alignItems: "end" }}>
          <div>
            <Button
              type={"tertiary"}
              color="red"
              onClick={() => {
                openConfirmDialog({
                  id: "deleteVariableDialog",
                  title: "Suppression",
                  content: "Voulez-vous vraiment supprimer cette condition ?",
                  onConfirm: () => {
                    element.outerHTML = "";
                    onChange(contentRef.current?.innerHTML || "");
                    closeDialog("deleteVariableDialog");
                    closeDrawer(`documentConditionDialog${toolbarId}`);
                  },
                });
              }}
            >
              Supprimer
            </Button>
          </div>
          <Input
            type="text"
            name={"Nom de la condition"}
            value={variable.name || ""}
            onChange={(newValue) => {
              variable.name = newValue;
            }}
          ></Input>
        </div>
      ),
      additionalClasses: ["medium"],
      confirmBeforeClose: true,

      content: (
        <ConditionGroupPicker
          level={0}
          sections={sections || []}
          current={variable.conditionGroup}
          onChange={(newValue) => {
            variable.conditionGroup = newValue;
          }}
        ></ConditionGroupPicker>
      ),
      onConfirm: async () => {
        await openConditionReplacementPicker({
          variable,
          onConfirm: () => {
            closeDrawer(`documentConditionDialog${toolbarId}`);
            closeDrawer(`ConditionReplacementPicker${toolbarId}`);
            onConfirm?.(variable);
          },
        });
      },
    });
  }
  async function openStatisticsReport({
    variable,
    onConfirm,
  }: {
    variable: DocumentVariable;
    onConfirm?: (newVariable: DocumentVariable) => void;
  }) {
    let report = variable.statisticsReport;
    if (!report) return;
    await openDrawer({
      id: "statisticsFiltersDrawer",
      title: "Filtres",
      additionalClasses: ["big"],
      trailing: (
        <div>
          <Input
            name={"Nom du rapport"}
            type="text"
            value={report.title || ""}
            onChange={(newValue) => {
              if (!report) return;
              report.title = newValue;
            }}
          ></Input>
        </div>
      ),
      content: (
        <div className="c1 gap10">
          <FiltersPicker
            report={report}
            variableSections={sections}
            current={report.groups}
            onChange={(newValue) => {
              if (!report) return;
              report.groups = newValue;
            }}
          ></FiltersPicker>
        </div>
      ),
      onConfirm: async () => {
        if (report?.title) variable.name = report?.title;

        onConfirm?.(variable);
        closeDrawer("statisticsFiltersDrawer");
      },
    });
  }

  async function openConditionReplacementPicker({
    variable,
    onConfirm,
  }: {
    variable: DocumentVariable;
    onConfirm?: (newValue: DocumentVariable) => void;
  }) {
    await openDrawer({
      id: `ConditionReplacementPicker${toolbarId}`,
      title: "Résultat de la condition",
      confirmBeforeClose: true,

      content: (
        <ConditionReplacementPicker
          sections={sections || []}
          current={variable}
          onChange={(newValue) => {
            variable = newValue;
          }}
        ></ConditionReplacementPicker>
      ),
      onConfirm: () => {
        onConfirm?.(variable);
      },
    });
  }

  function isAllowed(button: DocumentToolbarButtonType) {
    if (!allowedButtons?.includes(button)) return false;

    if (button == "save" && status == "validated") return false;
    let isValidatable = patientDocument?.template?.isValidatable == true;
    if (button == "validate" && status == "validated") return false;
    if (!isValidatable && button == "validate") return false;
    if (isValidatable && button == "print" && status != "validated")
      return false;

    return true;
  }

  if (allowedButtons.length == 1 && allowedButtons[0] == "variable")
    return (
      <Button
        type={"tertiary"}
        onClick={async () => {
          await openVariableSelect();
        }}
      >
        <TbVariablePlus size={16} />
      </Button>
    );

  return (
    <div id="documentEditorToolbar" className="p10">
      {isAllowed("format") && (
        <div>
          <select
            id="preview-format"
            onChange={(newValue) => {
              onFormatChange(newValue.target.value as DocumentFormat);
              setFormat(newValue.target.value as DocumentFormat);
            }}
          >
            <option value="a4">A4</option>
            <option value="a5" selected>
              A5
            </option>
          </select>
        </div>
      )}
      {isAllowed("size") && (
        <div>
          <select
            onChange={(e) => {
              // document.execCommand("styleWithCSS", false, "true");
              document.execCommand("fontSize", false, e.target.value);
            }}
          >
            <option value="auto" selected>
              Taille
            </option>
            <option value="1">8</option>
            <option value="2">12</option>
            <option value="3">14</option>
            <option value="4">16</option>
            <option value="5">20</option>
            <option value="6">25</option>
            <option value="7">30</option>
          </select>
        </div>
      )}
      {isAllowed("align") && (
        <div>
          <select
            className="formatting-btn toolbar-align"
            data-action="JustifyCenter"
            onChange={(e) => {
              document.execCommand(e.target.value, false, "");
            }}
          >
            <option value="auto" selected>
              Align.
            </option>
            <option value="JustifyLeft">Gauche</option>
            <option value="JustifyCenter">Centre</option>
            <option value="JustifyRight">Droite</option>
          </select>
        </div>
      )}

      {isAllowed("bold") && (
        <div
          id="toolbar-bold"
          className="formatting-btn bold"
          onClick={() => {
            document.execCommand("bold", false, "");
          }}
        >
          <img src="./images/bold-icon-black.png" />
        </div>
      )}

      {isAllowed("underline") && (
        <div
          id="toolbar-underline"
          className="formatting-btn bold"
          onClick={() => {
            document.execCommand("underline", false, "");
          }}
        >
          <img src="./images/underline-icon-black.png" />
        </div>
      )}

      {/* <div
            className="formatting-btn toolbar-align"
            data-action="JustifyLeft"
            onClick={() => {
              document.execCommand("JustifyLeft", false, "");
            }}
          >
            <img src="./images/align-left-icon-black.png" />
          </div> */}

      {/* <div
            className="formatting-btn toolbar-align"
            data-action="JustifyRight"
            onClick={() => {
              document.execCommand("JustifyRight", false, "");
            }}
          >
            <img src="./images/align-right-icon-black.png" />
          </div> */}
      {isAllowed("undo") && (
        <div
          className="formatting-btn undo"
          onClick={() => {
            document.execCommand("undo", false, "");
          }}
        >
          <LuUndo size={20}></LuUndo>
        </div>
      )}
      {isAllowed("redo") && (
        <div
          className="formatting-btn redo"
          onClick={() => {
            document.execCommand("redo", false, "");
          }}
        >
          <LuRedo size={20}></LuRedo>
        </div>
      )}

      {isAllowed("variable") && (
        <Button
          type={"tertiary"}
          onClick={async () => {
            await openVariableSelect();
          }}
        >
          Variable
        </Button>
      )}
      {isAllowed("html") && (
        <Button
          type={isHtmlMode == true ? "tertiary" : "secondary"}
          onClick={async () => {
            onHtmlModeChange?.(!isHtmlMode);
          }}
        >
          {isHtmlMode == true ? "Désactiver HTML" : "HTML"}
        </Button>
      )}

      {isAllowed("ai") && (
        <div style={{ display: "none" }}>
          <Button
            type="secondary"
            onClick={async () => {
              let selection = window.getSelection();
              let range = selection?.getRangeAt(0);

              await openDialog({
                id: `aiCompletionDialog${toolbarId}`,
                title: "Intelligence artificielle",

                content: (
                  <CompletionTypeChoiceList
                    onSelect={async (completionType) => {
                      let result: string | null | undefined = "No ai result";
                      result = compteRendu1;
                      // if (completionType == "summary") {
                      //   result = await apiFetch("general","aiQuery",
                      //     {
                      //       prompt: `Voici un compte rendu complet d'une hospitalisation d'un patient. Résume le moi et donne moi une conclusion en respectant bien l'HTML. ${
                      //         contentRef.current?.innerText || ""
                      //       }`,
                      //     },
                      //     "Erreur lors du test"
                      //   );
                      // }

                      let newNode = document.createElement("span");
                      newNode.innerHTML = `${result}`;

                      range?.insertNode(newNode);
                      range?.selectNodeContents(newNode);
                      selection?.removeAllRanges();
                      closeDialog(`aiCompletionDialog${toolbarId}`);
                    }}
                  ></CompletionTypeChoiceList>
                ),
              });
            }}
          >
            Générer par IA
          </Button>
        </div>
      )}

      {isAllowed("print") && (
        <Button
          isDisabled={patientDocument == null}
          type={"tertiary"}
          onClick={async () => {
            await apiFetch(
              "printingDemands",
              "addOne",
              {
                printingDemand: {
                  status: "pending",
                  creator: getUserId(),
                  service: USER.get()?.service,
                  patientDocument,
                },
              },
              "Erreur lors de la demande d'impression"
            );
          }}
        >
          Envoyer sécrétariat
        </Button>
      )}
      {isAllowed("save") && isHtmlMode != true && (
        <Button
          type={"tertiary"}
          // isDisabled={!hasChanged}
          onClick={async () => {
            await onSave?.();
          }}
        >
          Enregistrer
        </Button>
      )}
      {isAllowed("validate") && (
        <Button
          // isDisabled={!hasChanged}
          onClick={async () => {
            await openConfirmDialog({
              id: "validationDialog",
              title: "Validation",
              content:
                "Voulez-vous vraiment valider ce document ? Il ne sera plus modifiable après validation.",
              onConfirm: async () => {
                closeDialog("validationDialog");
                await onValidate?.();
                setStatus("validated");
              },
            });
          }}
        >
          Valider
        </Button>
      )}

      {isAllowed("print") && (
        <Button
          onClick={async () => {
            await printContent(
              document.getElementById("documentEditorPrintable")?.innerHTML,
              format,
              USER.get()?.service
            );

            await onPrint?.();
          }}
        >
          Imprimer
        </Button>
      )}

    </div>
  );
}

function CompletionTypeChoiceList({
  onSelect,
}: {
  onSelect: (variable: CompletionType) => void;
}) {
  return (
    <div className="c1 gap10">
      <div className="c1 gap30">
        <div className="grid c1 gap10">
          <div className="bold f16">Formulaire</div>
          {Object.values(completionTypes).map((type) => {
            return (
              <div
                key={uid()}
                className="card"
                onClick={() => {
                  onSelect(type.id);
                }}
              >
                {type.name}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { CompletionTypeChoiceList, DocumentEditorToolbar };
